// The geometry of a month calendar: Monday-first weeks, padded with the
// adjacent months' days so the columns line up under the weekday header.
//
// Data-free on purpose. The availability endpoint is asked for exactly the span
// this returns, so every box in the view has a real answer — including the
// leading and trailing days, which a calendar still shows.

import dayjs from '@app/utils/dayjs-setup';
import type { Dayjs } from 'dayjs';

export interface MonthGridDay {
  /** YYYY-MM-DD. */
  date: string;
  dayOfMonth: number;
  /** False for the leading/trailing days borrowed from the adjacent months. */
  inMonth: boolean;
  isToday: boolean;
}

export interface MonthGrid {
  /** First day of the grid (a Monday), YYYY-MM-DD — the range to ask the API for. */
  gridStart: string;
  /** Last day of the grid (a Sunday), YYYY-MM-DD. */
  gridEnd: string;
  /** Always 7 entries per week. */
  weeks: MonthGridDay[][];
}

const DATE_FORMAT = 'YYYY-MM-DD';

export const buildMonthGrid = (month: Dayjs): MonthGrid => {
  const monthStart = month.startOf('month');
  const monthEnd = month.endOf('month');
  // isoWeekday() is 1..7 for Monday..Sunday, so this backs up to the Monday of
  // the week the 1st falls in and runs to the Sunday of the week the last day does.
  const gridStart = monthStart.subtract(monthStart.isoWeekday() - 1, 'day');
  const gridEnd = monthEnd.add(7 - monthEnd.isoWeekday(), 'day');
  const today = dayjs().format(DATE_FORMAT);

  const weeks: MonthGridDay[][] = [];
  let week: MonthGridDay[] = [];
  for (let day = gridStart; !day.isAfter(gridEnd, 'day'); day = day.add(1, 'day')) {
    const date = day.format(DATE_FORMAT);
    week.push({
      date,
      dayOfMonth: day.date(),
      inMonth: day.isSame(monthStart, 'month'),
      isToday: date === today,
    });
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  }

  return { gridStart: gridStart.format(DATE_FORMAT), gridEnd: gridEnd.format(DATE_FORMAT), weeks };
};
